/**
 * User list grid — multi-context user management for ODL customer accounts.
 *
 * Server-backed via EntityGrid over odl-app/users/. Supports scoping to a
 * specific customer or site group via caller-supplied props; when unscoped the
 * ScopeSearchInput lets privileged users narrow by customer or site group
 * inline. The access label (all plants / all site groups / specific / staff /
 * super) derives from per-row is_staff, is_superuser, all_sitegroups_user, and
 * the row's customer.is_billboard flag. Staff users gain an additional
 * staff-role quick filter. Staff and customer admins can also view deleted
 * accounts and delete or restore a selection through the preflighted
 * lifecycle dialog.
 *
 * Contains:
 * - UserGrid      — EntityGrid over odl-app/users/ (default export)
 * - UserListItem  — mobile single-row card
 */

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Box,
  Button,
  Chip,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import FilterBar from '@sophocles/components/FilterBar';
import { buildStatusQuickFilter } from '@sophocles/components/FilterBar/statusQuickFilter';
import ScopeSearchInput from '@sophocles/components/ScopeSearchInput';
import {
  customerScopeChip,
  customerScopeProvider,
  siteGroupScopeChip,
  siteGroupScopeProvider,
} from '@sophocles/components/ScopeSearchInput/scopeProviders';
import UserAvatar from '@sophocles/components/UserAvatar';
import { EntityGrid } from '@sophocles/data-grids';
import GridLinkCell from '@sophocles/data-grids/components/cells/GridLinkCell';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import { SELECTION_ACTION_SX, resolveSelectionRows } from '@sophocles/data-grids/datasource/selectionRows';
import useGridPreferenceKey, { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import useGridSelectAll from '@sophocles/data-grids/datasource/useGridSelectAll';
import { getAuthUserCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import useUserPreference from '@sophocles/hooks/useUserPreference';
import { displayRoles, userPermCheck } from '@sophocles/utils';
import { formatDateTime } from '@sophocles/utils/formatUtils';
import { LIFECYCLE_ACTIONS } from '@sophocles/views/users/lifecycle/userLifecycle';
import UserLifecycleDialog from '@sophocles/views/users/lifecycle/UserLifecycleDialog';
import useUserLifecycleAction from '@sophocles/views/users/lifecycle/useUserLifecycleAction';

const NO_IDS = [];

// Account state cell: a deleted account reads "Deleted" ahead of its
// (always false) is_active flag.
function AccountStatusCell({ row, t }) {
  if (row.is_deleted) return <GridStatusCell color="bad" label={t('general.deleted', 'Deleted')} />;
  return (
    <GridStatusCell
      color={row.is_active ? 'ok' : 'bad'}
      label={row.is_active ? t('general.active') : t('general.inactive')}
    />
  );
}

function UserListItem({ row, isBillboard, t }) {
  const accessLabel = (() => {
    if (row.all_sitegroups_user) {
      if (row.is_staff) return row.is_superuser ? t('userSettings.super') : t('userSettings.staff');
      return isBillboard ? t('userSettings.allPlants') : t('userSettings.allSiteGroups');
    }
    return isBillboard ? t('userSettings.specificPlants') : t('userSettings.specificSiteGroups');
  })();

  const roleLabel = displayRoles(row.groups ?? [], isBillboard, true, 3, t) || null;

  return (
    <ListItemButton
      sx={{
        width: '100%',
        py: 1.5,
        px: 2,
      }}
    >
      <UserAvatar user={row} size={28} sx={{ mr: 1.5, flexShrink: 0 }} />
      <ListItemText
        primary={`${row.first_name} ${row.last_name}`}
        secondary={row.email}
        primaryTypographyProps={{ fontWeight: 600 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1, flexShrink: 0 }}>
        {roleLabel ? <Chip size="small" variant="outlined" label={roleLabel} /> : null}
        <Chip size="small" variant="outlined" label={accessLabel} />
        <AccountStatusCell row={row} t={t} />
      </Stack>
    </ListItemButton>
  );
}

// Selection-bar Delete / Restore button.
function LifecycleActionButton({ action, onClick }) {
  const { t } = useTranslation();
  const isDelete = action === LIFECYCLE_ACTIONS.DELETE;
  return (
    <Button
      size="small"
      startIcon={isDelete ? <DeleteOutlineIcon /> : <RestoreIcon />}
      onClick={onClick}
      sx={SELECTION_ACTION_SX}
    >
      {isDelete ? t('actions.delete', 'Delete') : t('actions.restore', 'Restore')}
    </Button>
  );
}

/**
 * UserGrid – EntityGrid for user management.
 *
 * Props:
 *   customer       – plain JS object (optional, scopes to a customer)
 *   siteGroup      – plain JS object (optional, scopes to a site group)
 *   defaultStatus  – 'active' | 'inactive' | 'all' | 'deleted'
 *   persistScope   – sessionStorage key scope, which the customer type in scope
 *                    further divides (see useGridSessionKey)
 */
export default function UserGrid({ customer, siteGroup, defaultStatus = 'active', persistScope = 'userGrid' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user, isStaff, permissions } = useUser();
  const authCustomer = useSelector(getAuthUserCustomerSelector);
  const tenantInfo = useSelector(selectTenantInfo);

  const isBillboard = authCustomer.get('is_billboard', true);
  const canCreateUser = userPermCheck(user, 'pliny.add_customuser');
  // Delete/restore and the deleted-accounts view share pliny's CanDeleteUsers grant.
  const canDeleteUsers = permissions.canDeleteUsers;
  const canViewMultipleCustomers =
    userPermCheck(user, 'pliny.view_all_customers') || userPermCheck(user, 'pliny.view_other_customers');

  // Scoping from parent view (customer/sitegroup context)
  const scopedCustomerId = customer?.id ?? null;
  const scopedSiteGroupId = siteGroup?.id ?? null;
  const showCascadeFilter = !scopedCustomerId && canViewMultipleCustomers;

  // Own the grid apiRef so the ScopeSearchInput can drive the quick filter while
  // EntityGrid manages the grid. EntityGrid mounts only after the spec resolves,
  // so gridReady re-triggers the quick-filter sync on a cold load.
  const apiRef = useGridApiRef();
  const [gridReady, setGridReady] = useState(false);
  const [searchText, setSearchText, commitSearch] = useGridQuickFilter(apiRef, gridReady);
  const { onGridReady: captureRefresh, refresh: refreshGrid } = useGridRefreshRef();
  const handleGridReady = useCallback(
    api => {
      captureRefresh(api);
      setGridReady(true);
    },
    [captureRefresh],
  );

  const [statusFilter, setStatusFilter] = usePersistedState(useGridSessionKey(persistScope, 'status'), defaultStatus);
  const [customerFilter, setCustomerFilter] = usePersistedState(useGridSessionKey(persistScope, 'customer'), null);
  const [siteGroupFilter, setSiteGroupFilter] = usePersistedState(useGridSessionKey(persistScope, 'siteGroup'), null);
  // Staff-only role filter; always persisted but only applied to extraParams when isStaff
  const [staffRoleFilter, setStaffRoleFilter] = usePersistedState(useGridSessionKey(persistScope, 'staffRole'), null);
  // Resolved display labels for scope chips — ids are the truth; labels are cosmetic
  const [scopeLabels, setScopeLabels] = useState({ customer: null, site_group: null });

  const extraParams = useMemo(() => {
    const params = {};

    if (statusFilter === 'inactive') {
      params.is_active = false;
    } else if (statusFilter === 'active') {
      params.is_active = true;
    } else if (statusFilter === 'deleted' && canDeleteUsers) {
      // The backend only admits deleted rows when this exact key is present,
      // and only for staff / customer admins; anyone else sees the 'all' view.
      params.is_deleted = true;
    }
    // 'all' → no is_active filter (deleted accounts stay excluded server-side)

    const cid = scopedCustomerId ?? customerFilter;
    if (cid) params.customer_id = cid;

    const sgid = scopedSiteGroupId ?? siteGroupFilter;
    if (sgid) params.site_group_id = sgid;

    // Staff-only role filters — backend ignores them for non-staff, but we
    // gate them here too so non-staff extraParams stay clean
    if (isStaff) {
      if (staffRoleFilter === 'staff') params.is_staff = true;
      else if (staffRoleFilter === 'superuser') params.is_superuser = true;
    }

    return params;
  }, [
    statusFilter,
    canDeleteUsers,
    scopedCustomerId,
    scopedSiteGroupId,
    customerFilter,
    siteGroupFilter,
    staffRoleFilter,
    isStaff,
  ]);

  // Delete / restore from the selection bar. The dialog preflights the
  // targets and explains any it will skip (self, superusers, out of scope);
  // a completed action refreshes the grid and drops the selection.
  //
  // Select-all works as on the other grids: the action targets every row
  // the live filters match, server-side, and the bar's count is the server
  // total. The Deleted view holds only deleted rows and every other view
  // only live ones, so the status filter decides which action it offers.
  const selectAllFilters = useMemo(() => toJsonApiParams(extraParams), [extraParams]);
  const { selectAll: selectAllOption, onClear: clearSelectAll } = useGridSelectAll(extraParams);
  const [totalRowCount, setTotalRowCount] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const { run: runLifecycle, submitting: lifecycleSubmitting } = useUserLifecycleAction();
  const closeLifecycle = useCallback(() => setLifecycle(null), []);
  const confirmLifecycle = useCallback(() => {
    if (!lifecycle) return;
    runLifecycle(lifecycle.action, lifecycle.target).then(outcome => {
      if (!outcome) return;
      if (outcome.applied) {
        lifecycle.clearSelection?.();
        clearSelectAll();
        refreshGrid();
      }
      setLifecycle(null);
    });
  }, [lifecycle, runLifecycle, clearSelectAll, refreshGrid]);

  const selectionActions = useCallback(
    (selection, clearSelection, gridApiRef) => {
      const { selectAll, rows } = resolveSelectionRows(selection, gridApiRef);
      const openLifecycle = (action, target) => setLifecycle({ action, target, clearSelection });
      if (selectAll) {
        const action = statusFilter === 'deleted' ? LIFECYCLE_ACTIONS.RESTORE : LIFECYCLE_ACTIONS.DELETE;
        const target = { selectAll: true, filters: selectAllFilters, count: Math.max(totalRowCount ?? 0, 0) };
        return <LifecycleActionButton action={action} onClick={() => openLifecycle(action, target)} />;
      }
      const deletable = rows.filter(row => !row.is_deleted);
      const restorable = rows.filter(row => row.is_deleted);
      const idsOf = targets => ({ userIds: targets.map(row => row.id) });
      return (
        <>
          {deletable.length > 0 && (
            <LifecycleActionButton
              action={LIFECYCLE_ACTIONS.DELETE}
              onClick={() => openLifecycle(LIFECYCLE_ACTIONS.DELETE, idsOf(deletable))}
            />
          )}
          {restorable.length > 0 && (
            <LifecycleActionButton
              action={LIFECYCLE_ACTIONS.RESTORE}
              onClick={() => openLifecycle(LIFECYCLE_ACTIONS.RESTORE, idsOf(restorable))}
            />
          )}
        </>
      );
    },
    [statusFilter, selectAllFilters, totalRowCount],
  );

  // User-toggleable column visibility preferences. The customer column visibility
  // is context-driven (hidden when scoped to a specific customer) and excluded
  // from what's persisted so it doesn't fight the scope context.
  const [columnVisibilityPrefs, setColumnVisibilityPrefs] = useUserPreference(
    useGridPreferenceKey(persistScope, 'columnVisibility.v2'),
    {},
  );

  const columnVisibilityModel = useMemo(
    () => ({
      ...columnVisibilityPrefs,
      customer: !scopedCustomerId,
    }),
    [columnVisibilityPrefs, scopedCustomerId],
  );

  const handleColumnVisibilityChange = useCallback(
    newModel =>
      setColumnVisibilityPrefs(Object.fromEntries(Object.entries(newModel).filter(([key]) => key !== 'customer'))),
    [setColumnVisibilityPrefs],
  );

  const columnVisibility = useMemo(
    () => ({ model: columnVisibilityModel, onChange: handleColumnVisibilityChange }),
    [columnVisibilityModel, handleColumnVisibilityChange],
  );

  // Build the edit URL based on scope
  const getUserUrl = useCallback(
    userId => {
      if (scopedSiteGroupId && scopedCustomerId) {
        return `/c/${scopedCustomerId}/plant/${scopedSiteGroupId}/users/${userId}/`;
      }
      if (scopedCustomerId) {
        return `/c/${scopedCustomerId}/users/${userId}/`;
      }
      return `/support/users/${userId}/`;
    },
    [scopedCustomerId, scopedSiteGroupId],
  );

  const getNewUserUrl = useCallback(() => {
    if (scopedSiteGroupId && scopedCustomerId) {
      return `/c/${scopedCustomerId}/plant/${scopedSiteGroupId}/users/new`;
    }
    if (scopedCustomerId) {
      return `/c/${scopedCustomerId}/users/new`;
    }
    return '/support/users/new';
  }, [scopedCustomerId, scopedSiteGroupId]);

  // Render overrides on top of descriptor column defaults. Sizing, order, and
  // headers come from the descriptor; overrides here are intentional display
  // decisions only.
  const fieldOverrides = useMemo(
    () => ({
      first_name: {
        display: 'flex',
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', width: '100%' }}>
            <UserAvatar user={row} size={28} sx={{ flexShrink: 0 }} />
            <GridLinkCell to={getUserUrl(row.id)} sx={{ overflow: 'hidden' }}>
              <Typography noWrap sx={{ color: 'primary.main' }}>
                {row.first_name}
                {row.last_name ? ` ${row.last_name}` : ''}
              </Typography>
            </GridLinkCell>
          </Box>
        ),
      },
      is_active: {
        renderCell: ({ row }) => <AccountStatusCell row={row} t={t} />,
      },
      last_login: {
        valueFormatter: value => (value ? formatDateTime(value, user) : t('general.never', 'Never')),
      },
      // Access label: derives from the row's all_sitegroups_user, is_staff,
      // is_superuser, and the per-row customer.is_billboard flag so billboard
      // and non-billboard customers render the right terminology.
      all_sitegroups_user: {
        valueGetter: (_value, row) => {
          const rowBillboard = row.customer?.is_billboard ?? isBillboard;
          if (row.all_sitegroups_user) {
            if (row.is_staff) return row.is_superuser ? t('userSettings.super') : t('userSettings.staff');
            return rowBillboard ? t('userSettings.allPlants') : t('userSettings.allSiteGroups');
          }
          return rowBillboard ? t('userSettings.specificPlants') : t('userSettings.specificSiteGroups');
        },
      },
      groups: {
        valueGetter: (_value, row) => {
          const rowBillboard = row.customer?.is_billboard ?? isBillboard;
          return displayRoles(row.groups ?? [], rowBillboard, true, 3, t) || t('general.none', 'None');
        },
      },
    }),
    [t, user, isBillboard, getUserUrl],
  );

  // Scope search providers and chips — offered only when not scoped to a customer.
  // One pair of handlers serves both apply surfaces — the typed suggestions
  // and the More-filters pick-lists — so either path yields the same chip.
  const handleScopeCustomerChange = useCallback(
    selection => {
      setCustomerFilter(selection?.id ?? null);
      setSiteGroupFilter(null);
      setScopeLabels({ customer: selection?.label ?? null, site_group: null });
    },
    [setCustomerFilter, setSiteGroupFilter],
  );
  const handleScopeSiteGroupChange = useCallback(
    selection => {
      setSiteGroupFilter(selection?.id ?? null);
      setScopeLabels(prev => ({ ...prev, site_group: selection?.label ?? null }));
    },
    [setSiteGroupFilter],
  );

  const scopeProviders = useMemo(
    () =>
      showCascadeFilter
        ? [
            customerScopeProvider({
              label: tenantInfo.customer_term,
              isActive: () => Boolean(customerFilter),
              applyValue: handleScopeCustomerChange,
            }),
            siteGroupScopeProvider({
              label: t('siteGroups.siteGroup'),
              getCustomerId: () => customerFilter,
              isActive: () => Boolean(siteGroupFilter),
              applyValue: handleScopeSiteGroupChange,
            }),
          ]
        : [],
    [
      showCascadeFilter,
      customerFilter,
      siteGroupFilter,
      handleScopeCustomerChange,
      handleScopeSiteGroupChange,
      tenantInfo.customer_term,
      t,
    ],
  );

  const scopeChips = useMemo(() => {
    if (!showCascadeFilter) return [];
    const out = [];
    if (customerFilter) {
      out.push(
        customerScopeChip({
          label: tenantInfo.customer_term,
          displayValue: scopeLabels.customer || `#${customerFilter}`,
          onRemove: () => {
            setCustomerFilter(null);
            setSiteGroupFilter(null);
            setScopeLabels({ customer: null, site_group: null });
          },
        }),
      );
    }
    if (siteGroupFilter) {
      out.push(
        siteGroupScopeChip({
          label: t('siteGroups.siteGroup'),
          displayValue: scopeLabels.site_group || `#${siteGroupFilter}`,
          onRemove: () => {
            setSiteGroupFilter(null);
            setScopeLabels(prev => ({ ...prev, site_group: null }));
          },
        }),
      );
    }
    return out;
  }, [
    showCascadeFilter,
    customerFilter,
    siteGroupFilter,
    scopeLabels,
    setCustomerFilter,
    setSiteGroupFilter,
    tenantInfo.customer_term,
    t,
  ]);

  const scopeSearch = showCascadeFilter ? (
    <ScopeSearchInput
      value={searchText}
      onValueChange={setSearchText}
      chips={scopeChips}
      providers={scopeProviders}
      placeholder={t('userSettings.searchUsers', 'Search users')}
      onSubmit={commitSearch}
    />
  ) : undefined;

  // Quick filter bar — status visible to all; staff-role filter gated to staff
  const quickFilters = useMemo(() => {
    const filters = [
      buildStatusQuickFilter({
        t,
        value: statusFilter,
        onChange: setStatusFilter,
        inactiveValue: 'inactive',
        inactiveLabel: t('general.inactive'),
        extraOptions: canDeleteUsers ? [{ value: 'deleted', label: t('general.deleted', 'Deleted') }] : [],
      }),
    ];

    if (isStaff) {
      filters.push({
        key: 'staffRole',
        label: t('userSettings.staffRole', 'Staff Role'),
        type: 'single',
        allLabel: t('general.all', 'All'),
        options: [
          { value: 'staff', label: t('userSettings.staff') },
          { value: 'superuser', label: t('userSettings.super') },
        ],
        value: staffRoleFilter,
        onChange: setStaffRoleFilter,
      });
    }

    return filters;
  }, [t, statusFilter, isStaff, canDeleteUsers, staffRoleFilter, setStatusFilter, setStaffRoleFilter]);

  const handleResetAll = useCallback(() => {
    setStatusFilter('active');
    setStaffRoleFilter(null);
    handleScopeCustomerChange(null);
  }, [setStatusFilter, setStaffRoleFilter, handleScopeCustomerChange]);

  const toolbarContent = <FilterBar quickFilters={quickFilters} onResetAll={handleResetAll} />;

  const renderMobileCard = useCallback(
    row => <UserListItem row={row} isBillboard={isBillboard} t={t} />,
    [isBillboard, t],
  );

  return (
    <>
      <EntityGrid
        basePath="users"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          searchPlaceholder: t('userSettings.searchUsers', 'Search users'),
          searchSlot: scopeSearch,
          persistKey: persistScope,
          infiniteScroll: true,
          gridApiRef: apiRef,
          onGridReady: handleGridReady,
          selection: canDeleteUsers
            ? { enabled: true, actions: selectionActions, selectAll: selectAllOption }
            : undefined,
          onTotalRowCountChange: setTotalRowCount,
          onRowClick: isMobile ? ({ row }) => navigate(getUserUrl(row.id)) : undefined,
          createAction: canCreateUser
            ? {
                label: `${t('general.create', 'Create')} ${t('userSettings.user', 'User')}`,
                onClick: () => navigate(getNewUserUrl()),
              }
            : undefined,
          toolbarContent,
          mobile: { renderCard: renderMobileCard },
          columnVisibility,
          countNoun: t('general.users', 'users'),
        }}
      />
      <UserLifecycleDialog
        open={lifecycle != null}
        onClose={closeLifecycle}
        onConfirm={confirmLifecycle}
        action={lifecycle?.action ?? LIFECYCLE_ACTIONS.DELETE}
        userIds={lifecycle?.target.userIds ?? NO_IDS}
        selectAll={lifecycle?.target.selectAll ?? false}
        filters={lifecycle?.target.filters}
        count={lifecycle?.target.count ?? 0}
        submitting={lifecycleSubmitting}
      />
    </>
  );
}
